import { useState } from 'react';
import { Play } from 'lucide-react';
import { L7_VIDEO } from '@/data/landingMedia';
import '@/styles/landingL7.css';

/* Home-page video section.
   ---------------------------------------------------------------------------
   Click-to-play facade: the poster is just an image until someone presses play,
   at which point the embed is mounted with autoplay. That means no YouTube or
   Vimeo script — and no third-party cookies — load for the majority of visitors
   who never press it, and the home page keeps its current load profile.

   Renders nothing at all when no URL is configured. A play button with nothing
   behind it is worse than no section, so this stays invisible until
   `L7_VIDEO.url` in data/landingMedia.ts is filled in. */

/** Turn a watch/share URL into an autoplaying embed URL. Returns null for a raw file. */
function embedUrl(url: string): string | null {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0&modestbranding=1`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1&title=0&byline=0`;
  return null;
}

export default function L7Video() {
  const { url, poster, eyebrow, title, lead } = L7_VIDEO;
  const [playing, setPlaying] = useState(false);
  if (!url) return null;

  const embed = embedUrl(url);

  return (
    <div className="l7">
      <section className="l7-sec l7-sec--paper">
        <div className="l7-wrap">
          <div className="l7-head">
            <span className="l7-eyebrow">{eyebrow}</span>
            <h2>{title}</h2>
            {lead && <p className="l7-lead">{lead}</p>}
          </div>

          <div className="l7-figure" style={{ position: 'relative', aspectRatio: '16 / 9', maxWidth: 940, margin: '0 auto' }}>
            {playing ? (
              embed ? (
                <iframe
                  src={embed}
                  title={typeof title === 'string' ? title : 'Video'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                />
              ) : (
                <video src={url} poster={poster} controls autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              )
            ) : (
              <button
                onClick={() => setPlaying(true)}
                aria-label="Play video"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', padding: 0, border: 0, cursor: 'pointer', background: 'none' }}
              >
                <img src={poster} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <span style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(18,47,82,.15), rgba(18,47,82,.55))' }} />
                <span
                  style={{
                    position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)',
                    width: 84, height: 84, borderRadius: '50%', background: 'var(--acc)', color: 'var(--on-acc)',
                    display: 'grid', placeItems: 'center', boxShadow: '0 20px 46px -16px rgba(0,0,0,.55)',
                  }}
                >
                  <Play size={32} strokeWidth={2.4} style={{ marginLeft: 4 }} fill="currentColor" />
                </span>
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
